import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import toast from 'react-hot-toast';
import { getApiUrl } from '../config';

function Social() {
  const [socials, setSocials] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ name: '', url: '', image: null });
  const [imagePreview, setImagePreview] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchSocials();
  }, []);

  const fetchSocials = async () => {
    try {
      const res = await fetch(getApiUrl('/api/info/social'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setSocials(data);
      }
    } catch (error) {
      console.error('Error fetching socials:', error);
      toast.error('خطا در دریافت شبکه‌های اجتماعی');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const formDataToSend = new FormData();
    formDataToSend.append('name', formData.name);
    formDataToSend.append('url', formData.url);
    
    if (formData.image) {
      formDataToSend.append('image', formData.image);
    }
    
    if (editing && formData.currentImage) {
      formDataToSend.append('currentImage', formData.currentImage);
    }

    try {
      const url = editing 
        ? getApiUrl(`/api/info/social/${editing.id}`)
        : getApiUrl('/api/info/social');
      
      const method = editing ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formDataToSend
      });

      if (res.ok) {
        toast.success(editing ? 'شبکه اجتماعی با موفقیت ویرایش شد' : 'شبکه اجتماعی با موفقیت اضافه شد');
        setOpenDialog(false);
        resetForm();
        fetchSocials();
      } else {
        const error = await res.json();
        toast.error(error.error || 'خطا در ذخیره شبکه اجتماعی');
      }
    } catch (error) {
      console.error('Error saving social:', error);
      toast.error('خطا در ذخیره شبکه اجتماعی');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا از حذف این شبکه اجتماعی اطمینان دارید؟')) return;

    try {
      const res = await fetch(getApiUrl(`/api/info/social/${id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (res.ok) {
        toast.success('شبکه اجتماعی با موفقیت حذف شد');
        fetchSocials();
      } else {
        toast.error('خطا در حذف شبکه اجتماعی');
      }
    } catch (error) {
      console.error('Error deleting social:', error);
      toast.error('خطا در حذف شبکه اجتماعی');
    }
  };

  const handleEdit = (social) => {
    setEditing(social);
    setFormData({
      name: social.name,
      url: social.url,
      image: null,
      currentImage: social.image
    });
    setImagePreview(social.image ? `${getApiUrl('')}${social.image}` : null);
    setOpenDialog(true);
  };

  const resetForm = () => {
    setFormData({ name: '', url: '', image: null });
    setImagePreview(null);
    setEditing(null);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fa-IR');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">مدیریت شبکه‌های اجتماعی</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          افزودن شبکه اجتماعی جدید
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>نام</TableCell>
              <TableCell>آدرس</TableCell>
              <TableCell>تصویر</TableCell>
              <TableCell>عملیات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {socials.map((social) => (
              <TableRow key={social.id}>
                <TableCell>{social.name}</TableCell>
                <TableCell>{social.url}</TableCell>
                <TableCell>
                  {social.image && (
                    <img 
                      src={`${getApiUrl(`/api/media/download?path=${social.image.split('/').pop()}`)}`} 
                      alt={social.name}
                      style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEdit(social)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(social.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'ویرایش شبکه اجتماعی' : 'افزودن شبکه اجتماعی جدید'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              label="نام شبکه اجتماعی"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="آدرس"
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              margin="normal"
              required
            />
            
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={{ marginTop: '16px' }}
            />

            {imagePreview && (
              <Box sx={{ mt: 2 }}>
                <img 
                  src={imagePreview} 
                  alt="Preview" 
                  style={{ maxWidth: '200px', maxHeight: '200px' }} 
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>لغو</Button>
            <Button type="submit" variant="contained">
              {editing ? 'ویرایش' : 'افزودن'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Social; 